import { ApolloError, useQuery } from '@apollo/client';
import type { User } from '@stytch/vanilla-js';
import { createContext, useContext } from 'react';
import { assert } from '../errors';
import { Experiments } from '../experiments';
import {
    AtomicStatusType,
    CurrentUserProfileDocument,
    FeatureFlagFieldsFragment,
    IncomingPartnershipInvite,
    MembershipFieldsFragment,
    OutgoingPartnershipInvite,
    PartnerInfo,
    PlentyAccountStatus,
    PlentyJointAccountStatus,
    RelationshipStatus,
    UserRole,
} from '../graphql/generated';
import { NotificationSettings } from '../notifications';
import {
    AtomicStatusTypeInfo,
    GoalPreferences,
    InvestmentProfile,
    OnboardingData,
    RecommendedRiskScoreByHorizon,
    SpendingComparisonInputs,
} from '../types';
import { DT, dt } from '../util/dateTime';

// For now, just use average millenial age.
const defaultDOB = '1988-01-01';

export interface UserInfo {
    id: string;
    email: string;
    firstName: string | undefined;
    lastName: string | undefined;
    avatar: string | undefined;
    dob: string | null | undefined;
    annualIncome: number | undefined;
    fullAtomicOnboardingFinished: boolean;
    summaryPageFinished: boolean;
    recommendedRiskScoreByHorizon:
        | RecommendedRiskScoreByHorizon
        | null
        | undefined;
    address1: string | null | undefined;
    address2: string | null | undefined;
    locality: string | null | undefined;
    region: string | null | undefined;
    postalCode: string | null | undefined;
    phone: string | null | undefined;
    preferredName: string | undefined;
    taxIdToken: string | null | undefined;
    trustedContactFirstName: string | null | undefined;
    trustedContactLastName: string | null | undefined;
    trustedContactEmail: string | null | undefined;
    trustedContactPhone: string | null | undefined;
    onboardingData: OnboardingData | undefined;
    investmentProfile: InvestmentProfile | undefined;
    atomicStatus: AtomicStatusType | null | undefined;
    atomicStatusInfo: AtomicStatusTypeInfo | null | undefined;
    age: number;
    finalDate: string;
    isAdmin: boolean;
    isEarlyAccessMember: boolean;
    plentyAccountStatus: PlentyAccountStatus;
    role: UserRole;
    marketingEmailOptIn: boolean | null | undefined;
    goalPreferences: GoalPreferences;
    spendingComparisonInputs: SpendingComparisonInputs;
    experiments: Experiments;
    monthlyNetIncome: number | undefined;
    combinedOrIndividualMonthlyNetIncome: number;
    createdAt: string;
    lastViewedTransactionEntry: { id: string; date: string } | null | undefined;
    notificationSettings: NotificationSettings;
    isFullyClosed: boolean;
}

export type ClientPartnerInfo = PartnerInfo & { isConfirmed: boolean };

export interface PartnershipInfo {
    activePartnershipId?: string | null;
    partnerInfo?: ClientPartnerInfo | null;
    incomingPartnershipInvite?: IncomingPartnershipInvite | null;
    outgoingPartnershipInvite?: OutgoingPartnershipInvite | null;
    plentyJointAccountStatus?: PlentyJointAccountStatus | null;
    isInvitedPartner?: boolean | null;
}

export interface UserData extends PartnershipInfo {
    isLoading: boolean;
    stytchUser?: User;
    userInfo?: UserInfo;
    membership?: MembershipFieldsFragment | null;
    error?: ApolloError;
    featureFlags?: FeatureFlagFieldsFragment | null;
}

/**
 * @deprecated All components other than AuthPageWrapper should use useCurrentUser
 */
export const useCommonUserData = ({
    user,
    isInitialized,
}: {
    user: User | null;
    isInitialized: boolean;
}): UserData => {
    const { data, loading, error } = useQuery(CurrentUserProfileDocument, {
        skip: !isInitialized || !user,
    });

    if (!isInitialized) {
        return { isLoading: true };
    }
    if (!user || error) {
        return { isLoading: false, error };
    }
    if (loading) {
        return { stytchUser: user, isLoading: true };
    }
    const userProfile = data?.currentUserProfile;
    const featureFlags = data?.featureFlags;

    if (!userProfile) {
        return { stytchUser: user, isLoading: false };
    }

    const {
        id,
        firstName,
        lastName,
        avatar,
        dob,
        annualIncome,
        fullAtomicOnboardingFinished,
        summaryPageFinished,
        recommendedRiskScoreByHorizon,
        address1,
        address2,
        locality,
        region,
        postalCode,
        phone,
        preferredName,
        taxIdToken,
        trustedContactFirstName,
        trustedContactLastName,
        trustedContactEmail,
        trustedContactPhone,
        onboardingData,
        investmentProfile,
        atomicStatus,
        atomicStatusInfo,
        plentyAccountStatus,
        role,
        marketingEmailOptIn,
        goalPreferences,
        spendingComparisonInputs,
        experiments,
        monthlyNetIncome,
        createdAt,
        lastViewedTransactionEntry,
        notificationSettings,
        isFullyClosed,
    } = userProfile;

    const age = 1 + dt().diff(dt(dob || defaultDOB), 'years');
    const finalDate = dob
        ? dt(dob).add(100, 'years').toDateStr()
        : dt().add(50, 'years').toDateStr();

    // HACK: Promotions should be stored server-side
    const isEarlyAccessMember =
        dt(userProfile.createdAt).toDateStr() <= '2024-02-29';

    const activePartnershipId = data.currentUserActivePartnershipId ?? null;

    let partnerInfo: ClientPartnerInfo | null = null;
    if (data.currentUserActivePartnerInfo) {
        partnerInfo = {
            ...data.currentUserActivePartnerInfo,
            isConfirmed: true,
        };
    } else if (data.currentUserUnconfirmedPartnerInfo) {
        partnerInfo = {
            ...data.currentUserUnconfirmedPartnerInfo,
            isConfirmed: false,
        };
    }

    const isInvitedPartner =
        !!activePartnershipId &&
        !!data.currentUserIncomingPartnershipInvite?.isAccepted &&
        data.currentUserIncomingPartnershipInvite?.partnershipId ===
            activePartnershipId;

    const combinedOrIndividualMonthlyNetIncome =
        partnerInfo?.incomeSharingMode === 'COMBINED'
            ? (monthlyNetIncome || 0) + (partnerInfo?.monthlyNetIncome || 0)
            : monthlyNetIncome || 0;

    return {
        isLoading: false,
        stytchUser: user,
        userInfo: {
            // from profile
            id,
            email: user.emails[0].email,
            firstName: firstName ?? undefined,
            lastName: lastName ?? undefined,
            preferredName: preferredName ?? undefined,
            dob,
            annualIncome: annualIncome ?? undefined,
            avatar: avatar ?? undefined,
            fullAtomicOnboardingFinished: !!fullAtomicOnboardingFinished,
            summaryPageFinished: !!summaryPageFinished,
            recommendedRiskScoreByHorizon,
            address1,
            address2,
            locality,
            region,
            postalCode,
            phone,
            taxIdToken,
            trustedContactFirstName,
            trustedContactLastName,
            trustedContactEmail,
            trustedContactPhone,
            onboardingData: onboardingData || undefined,
            investmentProfile: investmentProfile || undefined,
            atomicStatus,
            atomicStatusInfo,
            plentyAccountStatus,
            createdAt,
            // derived
            age,
            finalDate,
            isAdmin: role === 'ADMIN',
            isEarlyAccessMember,
            role,
            marketingEmailOptIn,
            goalPreferences: goalPreferences ?? {},
            spendingComparisonInputs: spendingComparisonInputs ?? {},
            experiments,
            monthlyNetIncome: monthlyNetIncome ?? undefined,
            combinedOrIndividualMonthlyNetIncome,
            lastViewedTransactionEntry,
            notificationSettings,
            isFullyClosed: !!isFullyClosed,
        },
        activePartnershipId,
        partnerInfo,
        outgoingPartnershipInvite: data.currentUserOutgoingPartnershipInvite,
        incomingPartnershipInvite: data.currentUserIncomingPartnershipInvite,
        plentyJointAccountStatus: data.currentUserPlentyJointAccountStatus,
        isInvitedPartner,
        membership: data.currentUserMembership,
        featureFlags,
    };
};

export const ageOnDate = (dob: string, date: string | DT): number => {
    return dt(date).diff(dob, 'years');
};

export const getPreferredOrFirstName = ({
    firstName,
    preferredName,
}: UserInfo | PartnerInfo) => preferredName ?? firstName;

export const getPartnerName = (
    partnerInfo: PartnerInfo | null | undefined,
    defaultName: string | undefined = 'Your partner'
): string =>
    partnerInfo?.preferredName || partnerInfo?.firstName || defaultName;

export const labelByRelationshipStatus: Record<
    Exclude<RelationshipStatus, 'DIVORCED'>,
    string
> = {
    DATING: 'Dating',
    ENGAGED: 'Engaged',
    NEWLYWED: 'Newlywed (<2 years)',
    MARRIED: 'Married (>2 years)',
    CIVIL_PARTNERSHIP: 'Civil Partnership',
};

export interface CurrentUserContext extends PartnershipInfo {
    userInfo: UserInfo;
    membership: MembershipFieldsFragment | undefined | null;
    featureFlags: FeatureFlagFieldsFragment;
}

export const CurrentUserContext = createContext<CurrentUserContext | null>(
    null
);

export const useCurrentUser = (): CurrentUserContext => {
    const ctx = useContext(CurrentUserContext);
    assert(ctx, 'Value must be passed to CurrentUserContext');
    return ctx;
};

export const isSingleplayer = (userData: CurrentUserContext) => {
    return (
        !!userData.userInfo?.onboardingData?.isSingleplayer &&
        !userData.partnerInfo?.isConfirmed &&
        !userData.incomingPartnershipInvite &&
        !userData.outgoingPartnershipInvite
    );
};
